import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Search, FileText, Hash, Link2, Wrench, Shield, LogOut, Zap, Menu, X, Folder, Sparkles, Calendar, Brain, ChevronDown, Globe, Plus, Rocket, Share2, CalendarClock, User as UserIcon, Plug, LineChart, Bot, Telescope, Megaphone, Crown, MapPin, Palette, Gift } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import SiteFooter from "@/components/SiteFooter";
import OnboardingWizard from "@/components/OnboardingWizard";

const PRIMARY_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-link-dashboard" },
  { to: "/analytics", label: "Analytics", icon: LineChart, testid: "nav-link-analytics" },
  { to: "/autopilot", label: "Autopilot", icon: Rocket, testid: "nav-link-autopilot" },
  { to: "/scheduler", label: "Scheduler", icon: CalendarClock, testid: "nav-link-scheduler" },
  { to: "/onboarding", label: "Business Analyzer", icon: Sparkles, testid: "nav-link-onboarding" },
  { to: "/content-plan", label: "Content Calendar", icon: Calendar, testid: "nav-link-content-plan" },
  { to: "/llm-visibility", label: "LLM Visibility", icon: Brain, testid: "nav-link-llm" },
  { to: "/explorer", label: "Site Explorer", icon: Telescope, testid: "nav-link-explorer" },
  { to: "/fix-kit", label: "Auto-Fix Kit", icon: Wrench, testid: "nav-link-fix-kit" },
  { to: "/otto", label: "OTTO Live SEO", icon: Bot, testid: "nav-link-otto" },
  { to: "/integrations", label: "Integrations", icon: Plug, testid: "nav-link-integrations" },
  { to: "/social", label: "Social Channels", icon: Share2, testid: "nav-link-social" },
];

const SEO_NAV = [
  { to: "/audit", label: "SEO Audit", icon: Search, testid: "nav-link-audit" },
  { to: "/blog", label: "Blog Generator", icon: FileText, testid: "nav-link-blog" },
  { to: "/keywords", label: "Keywords", icon: Hash, testid: "nav-link-keywords" },
  { to: "/local-seo", label: "Local SEO", icon: MapPin, testid: "nav-link-local-seo" },
  { to: "/google-ads", label: "Google Ads", icon: Megaphone, testid: "nav-link-google-ads" },
  { to: "/ppc-leak", label: "Steal PPC Keywords", icon: Crown, testid: "nav-link-ppc-leak" },
  { to: "/backlinks", label: "Backlinks", icon: Link2, testid: "nav-link-backlinks" },
  { to: "/tools", label: "Free Tools", icon: Wrench, testid: "nav-link-tools" },
];

const AGENCY_NAV = [
  { to: "/whitelabel", label: "Agency / White-label", icon: Palette, testid: "nav-link-whitelabel" },
  { to: "/affiliate", label: "Earn 20% (Affiliate)", icon: Gift, testid: "nav-link-affiliate" },
];

export default function DashboardLayout({ children, title, subtitle, eyebrow, action }) {
  const { user, access, logout } = useAuth();
  const { projects, active, setActive } = useProjects();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const initials = (user?.name || user?.email || "U").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

  const renderNav = (items) => items.map((n) => (
    <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)} data-testid={n.testid}
             className={({ isActive }) =>
               `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                 isActive ? "bg-[#0055FF]/10 text-white border-l-2 border-[#0055FF] pl-[10px]" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
               }`
             }>
      <n.icon className="w-4 h-4" />
      {n.label}
    </NavLink>
  ));

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static z-40 inset-y-0 left-0 w-64 bg-[#0B0B0E] border-r border-zinc-800 transition-transform flex flex-col ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center justify-between px-6 border-b border-zinc-800 flex-shrink-0">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <div className="w-7 h-7 bg-[#0055FF] rounded-sm grid place-items-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-semibold">RankPilot</span>
          </Link>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-zinc-500" aria-label="Close menu">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Project switcher */}
        <div className="px-3 py-3 border-b border-zinc-800 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-2 px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-sm hover:border-zinc-700 transition-colors" data-testid="project-switcher">
                <Globe className="w-4 h-4 text-[#0055FF] flex-shrink-0" />
                <div className="min-w-0 flex-1 text-left">
                  <div className="text-xs text-zinc-500 uppercase tracking-widest leading-tight">Project</div>
                  <div className="text-sm text-zinc-200 truncate font-medium">
                    {active ? active.name : "No project"}
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-zinc-500 flex-shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-[#0B0B0E] border-zinc-800 rounded-sm w-56">
              <DropdownMenuLabel className="text-xs uppercase tracking-widest text-zinc-500 font-normal">Switch project</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-zinc-800" />
              {projects.length === 0 && <div className="px-2 py-3 text-sm text-zinc-500">No projects yet</div>}
              {projects.map((p) => (
                <DropdownMenuItem key={p.id} onClick={() => setActive(p.id)}
                                  className="text-zinc-300 focus:bg-zinc-900 focus:text-white cursor-pointer"
                                  data-testid={`project-switch-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{p.name}</div>
                    <div className="text-xs text-zinc-500 truncate">{p.domain}</div>
                  </div>
                  {p.id === active?.id && <span className="ml-2 w-2 h-2 rounded-full bg-[#0055FF]" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem onClick={() => navigate("/projects")} className="text-zinc-300 focus:bg-zinc-900 focus:text-white" data-testid="project-manage">
                <Folder className="w-4 h-4 mr-2" /> Manage projects
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/onboarding")} className="text-zinc-300 focus:bg-zinc-900 focus:text-white" data-testid="project-new">
                <Plus className="w-4 h-4 mr-2" /> New project
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <nav className="p-3 space-y-0.5 flex-1 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-600 px-3 mb-1.5 mt-1">Growth Engine</div>
          {renderNav(PRIMARY_NAV)}
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-600 px-3 mb-1.5 mt-5">SEO Toolkit</div>
          {renderNav(SEO_NAV)}
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-600 px-3 mb-1.5 mt-5">Agency</div>
          {renderNav(AGENCY_NAV)}
          {user?.role === "admin" && (
            <>
              <div className="h-px bg-zinc-800 my-3" />
              <NavLink to="/admin" data-testid="nav-link-admin"
                       className={({ isActive }) =>
                         `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                           isActive ? "bg-[#F59E0B]/10 text-white border-l-2 border-[#F59E0B] pl-[10px]" : "text-zinc-400 hover:text-white hover:bg-zinc-900"
                         }`
                       }>
                <Shield className="w-4 h-4" />
                Admin
              </NavLink>
            </>
          )}
        </nav>

        <div className="p-4 border-t border-zinc-800 flex-shrink-0">
          {access?.unlimited ? (
            <div className="text-xs text-zinc-500 leading-relaxed" data-testid="sidebar-plan">
              <span className="label-eyebrow">PLAN</span>
              <div className="mt-1 text-[#10B981] font-medium flex items-center gap-1.5">
                <Shield className="w-3 h-3" /> Unlimited
              </div>
            </div>
          ) : access?.status === "active" ? (
            <div className="text-xs text-zinc-500 leading-relaxed" data-testid="sidebar-plan">
              <span className="label-eyebrow">PLAN</span>
              <div className="mt-1 text-zinc-200 font-medium capitalize">{access.tier}</div>
              <div className="text-[10px] text-zinc-600 mt-0.5">{access.days_left} days left</div>
              <Link to="/upgrade" className="text-[#3377FF] hover:text-white text-xs mt-2 inline-block" data-testid="sidebar-manage">Manage →</Link>
            </div>
          ) : access?.status === "trial" ? (
            <div className="text-xs leading-relaxed" data-testid="sidebar-plan">
              <span className="label-eyebrow">FREE TRIAL</span>
              <div className="mt-1 text-zinc-200 font-medium">{access.days_left} {access.days_left === 1 ? "day" : "days"} left</div>
              <Link to="/upgrade" className="mt-2 inline-flex items-center gap-1 text-xs bg-[#0055FF] hover:bg-[#3377FF] text-white px-2.5 py-1.5 rounded-sm transition-colors" data-testid="sidebar-upgrade">
                Upgrade →
              </Link>
            </div>
          ) : access?.locked ? (
            <div className="text-xs leading-relaxed" data-testid="sidebar-plan">
              <span className="label-eyebrow text-red-400">EXPIRED</span>
              <div className="mt-1 text-red-400 font-medium">Trial ended</div>
              <Link to="/upgrade" className="mt-2 inline-flex items-center gap-1 text-xs bg-red-500 hover:bg-red-400 text-white px-2.5 py-1.5 rounded-sm transition-colors" data-testid="sidebar-upgrade-expired">
                Unlock now →
              </Link>
            </div>
          ) : (
            <div className="text-xs text-zinc-500 leading-relaxed" data-testid="sidebar-plan">
              <span className="label-eyebrow">PLAN</span>
              <div className="mt-1 text-zinc-300 font-medium">—</div>
            </div>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 lg:pl-0">
        <header className="h-16 sticky top-0 z-30 border-b border-zinc-800 bg-[#09090B]/85 backdrop-blur-xl flex items-center justify-between px-4 lg:px-8" data-testid="dashboard-topbar">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden text-zinc-400" aria-label="Open menu">
              <Menu className="w-5 h-5" />
            </button>
            <span className="text-xs text-zinc-500 font-mono uppercase tracking-widest hidden sm:inline">RankPilot Console</span>
          </div>
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:bg-zinc-900 rounded-sm px-2 py-1 transition-colors" data-testid="user-menu">
                  <Avatar className="w-8 h-8 rounded-sm">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#0055FF]/20 text-[#3377FF] rounded-sm text-xs">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm text-zinc-300">{user?.name || user?.email}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                <DropdownMenuLabel className="text-zinc-400 font-normal">
                  <div className="text-xs text-zinc-500">Signed in as</div>
                  <div className="text-sm text-zinc-200 truncate max-w-[200px]">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem onClick={() => navigate("/account")} className="text-zinc-300 focus:bg-zinc-900 focus:text-white" data-testid="user-menu-account">
                  <UserIcon className="w-4 h-4 mr-2" /> Account settings
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/projects")} className="text-zinc-300 focus:bg-zinc-900 focus:text-white" data-testid="user-menu-projects">
                  <Folder className="w-4 h-4 mr-2" /> My projects
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="text-zinc-300 focus:bg-zinc-900 focus:text-white" data-testid="user-menu-admin">
                    <Shield className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="text-zinc-300 focus:bg-zinc-900 focus:text-white" data-testid="user-menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-10 max-w-[1400px]">
          {/* Email verification banner — soft reminder */}
          {user && user.email_verified === false && (
            <EmailVerifyBanner email={user.email} />
          )}
          {/* Trial countdown banner */}
          {access?.status === "trial" && access.days_left <= 3 && (
            <div className="mb-6 border border-amber-700/40 bg-amber-950/30 rounded-sm px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap" data-testid="trial-banner">
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span className="text-amber-200">
                  <strong>{access.days_left} {access.days_left === 1 ? "day" : "days"} left</strong> in your free trial. After it ends, all features lock until you upgrade.
                </span>
              </div>
              <Link to="/upgrade" className="text-xs font-medium bg-amber-500 hover:bg-amber-400 text-amber-950 px-3 py-1.5 rounded-sm transition-colors whitespace-nowrap" data-testid="trial-banner-cta">
                See plans →
              </Link>
            </div>
          )}
          {(eyebrow || title || subtitle || action) && (
            <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                {eyebrow && <span className="label-eyebrow" data-testid="page-eyebrow">{eyebrow}</span>}
                {title && <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1" data-testid="page-title">{title}</h1>}
                {subtitle && <p className="text-zinc-400 mt-2 max-w-2xl">{subtitle}</p>}
              </div>
              {action}
            </div>
          )}
          {children}
          <SiteFooter />
        </main>
        <OnboardingWizard />
      </div>
    </div>
  );
}
